import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { match } from 'ts-pattern'

import { AsyncStateN, Redacted } from '@blade/std-lib'
import { isPasswordValid } from '@root/entities/auth/constants'
import {
  loginModel,
  storedUsernameAtom,
} from '@root/features/auth/authenticate'
import { backClickedAction } from '@root/features/initialization/atoms/common'
import {
  AuthButton,
  AuthInfoMessage,
  AuthModal,
  AuthModalHeading,
  AuthInput,
} from '@root/shared/ui/auth-modal'
import { InputPassword, ProfileIcon } from '@root/shared/ui-kit'

import { AuthHelp } from './auth-help'

import type {
  LoginMethod,
  LoginPayload,
} from '@root/features/auth/authenticate'

type FormValues = {
  username: string
  password: string
}

type Props = {
  loginMethod: LoginMethod
}

export const AuthLogin: React.FC<Props> = ({ loginMethod }) => {
  const { t } = useTranslation()
  const [storedUsername] = useAtom(storedUsernameAtom)
  const [requestState] = useAtom(loginModel.loginRequestState)

  const login = useAction(loginModel.loginAction)
  const backClicked = useAction(backClickedAction)

  useEffect(() => {
    if (
      (loginMethod.type === 'telegram' ||
        loginMethod.type === 'google' ||
        loginMethod.type === 'twitter') &&
      AsyncStateN.isError(requestState)
    ) {
      backClicked()
    }
  }, [requestState, loginMethod, backClicked])

  const { control, formState, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      username: storedUsername || '',
      password: '',
    },
  })

  const handleSubmitInternal = handleSubmit((values) => {
    const otp = undefined
    const loginPayload = match(loginMethod)
      .returnType<LoginPayload>()
      .with({ type: 'telegram' }, ({ code, token }) => ({
        type: 'telegram',
        code,
        token,
        password: Redacted.make(values.password),
        username: values.username,
        otp,
      }))
      .with({ type: 'google' }, ({ code }) => ({
        type: 'google',
        code,
        password: Redacted.make(values.password),
        username: values.username,
        otp,
      }))
      .with({ type: 'twitter' }, ({ code }) => ({
        type: 'twitter',
        code,
        password: Redacted.make(values.password),
        username: values.username,
        otp,
      }))
      .with({ type: 'username' }, () => ({
        type: 'username',
        password: Redacted.make(values.password),
        username: values.username,
        otp,
      }))
      .exhaustive()

    login(loginPayload)
  })

  if (
    (loginMethod.type === 'telegram' ||
      loginMethod.type === 'google' ||
      loginMethod.type === 'twitter') &&
    AsyncStateN.isLoading(requestState)
  ) {
    return (
      <AuthModal infoIcon={ProfileIcon} infoMessage={t('auth.info.wallets')}>
        <AuthInfoMessage
          title={t('common.loading')}
          description={t(
            loginMethod.type === 'telegram'
              ? 'auth.login-with-telegram-loading'
              : 'auth.login-with-google-loading',
          )}
          variant="loading"
        />
      </AuthModal>
    )
  }

  return (
    <AuthModal infoIcon={ProfileIcon} infoMessage={t('auth.info.wallets')}>
      <Root onSubmit={handleSubmitInternal}>
        <AuthModalHeading
          icon={ProfileIcon}
          title={t(
            loginMethod.type === 'username'
              ? 'auth.login-with-username'
              : 'auth.login-to-your-account',
          )}
          onBack={() => backClicked()}
          mobileInfoMessage={t('auth.info.wallets')}
        />
        <Group>
          {loginMethod.type === 'username' ? (
            <Controller
              rules={{ required: true }}
              control={control}
              name="username"
              render={({ field }) => (
                <AuthInput
                  {...field}
                  startContent={
                    <StartContent>{t('auth.username')}</StartContent>
                  }
                  autoCorrect="off"
                  autoFocus
                  spellCheck={false}
                  autoCapitalize="off"
                  autoComplete="username"
                />
              )}
            />
          ) : undefined}
          <Controller
            rules={{ required: true, validate: isPasswordValid }}
            control={control}
            name="password"
            render={({ field }) => (
              <InputPassword
                {...field}
                startContent={t('auth.password')}
                autoCorrect="off"
                spellCheck={false}
                autoCapitalize="off"
                autoComplete="off"
                size="m"
                css={css`
                  --input-background: var(--appearanceA-2);

                  &:focus-within {
                    --input-background-active: var(--appearanceA-5);
                  }
                `}
              />
            )}
          />
          <AuthHelp
            css={css`
              margin-bottom: 10px;
            `}
          />
        </Group>
        <AuthButton disabled={!formState.isValid} busy={requestState.loading}>
          {t('auth.sign-in')}
        </AuthButton>
      </Root>
    </AuthModal>
  )
}

const Root = styled.form`
  display: grid;
  height: 100%;
  gap: 18px;
`

const Group = styled.div`
  display: grid;
  gap: 12px;
  align-content: center;
`

const StartContent = styled.div`
  width: 70px;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
